/**
 * Dedicated /copilot route — full-page Copilot view.
 * Left: chat. Right: proposals review.
 * Staff-only (admin / school_admin / teacher). Students/parents are redirected
 * to a small read-only assistant card.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles } from 'lucide-react';
import CopilotChat from '@/components/copilot/CopilotChat';
import ProposalsPanel from '@/components/copilot/ProposalsPanel';
import CopilotFreeChat from '@/components/copilot/CopilotFreeChat';
import CopilotTierPanel from '@/components/copilot/CopilotTierPanel';
import api from '@/lib/api';

export default function CopilotPage() {
  const { userType, user, isAuthenticated } = useAuthStore();
  // Refresh trigger when chat creates a proposal — proposals panel listens via query key invalidation
  const [, setLastProposalAt] = useState(null);

  const { data: tier } = useQuery({
    queryKey: ['copilot-tier'],
    queryFn: () => api.get('/copilot/tier').then((r) => r.data).catch(() => null),
    enabled: isAuthenticated,
    staleTime: 300_000,
  });

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Free Copilot is for everyone. Copilot Pro — the half that reads live data
  // and writes proposals — is super-admins only, decided server-side by
  // core/rbac_hierarchy.py; this page only mirrors that answer.
  if (tier && tier.tier !== 'pro') {
    return (
      <div className="max-w-3xl mx-auto py-10 px-6 grid gap-6 lg:grid-cols-[1fr_320px]"
           data-testid="copilot-free-page">
        <Card className="lg:col-span-1 h-[560px] flex flex-col overflow-hidden">
          <CardHeader className="border-b py-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-violet-600" /> FWM Copilot
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700"
                    data-testid="copilot-page-tier-badge">Free</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden flex flex-col">
            <CopilotFreeChat />
          </CardContent>
        </Card>
        <Card className="h-fit">
          <CardContent className="p-0"><CopilotTierPanel tier={tier} /></CardContent>
        </Card>
      </div>
    );
  }

  const tenantId = user?.tenant_id || user?.tenantId || '';
  const role = userType === 'admin' && !user?.tenant_id ? 'super_admin'
                : userType === 'admin' ? 'school_admin' : userType;
  // Super-admins can scope to any tenant. For MVP, default to the first
  // tenant cookie / localStorage value the admin landed on. If none, ask
  // them to drill in via the Schools list rather than hard-fail.
  const effectiveTenant = tenantId
    || (role === 'super_admin' ? (localStorage.getItem('eventflow_last_tenant') || '') : '');

  if (!effectiveTenant) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-6" data-testid="copilot-needs-tenant">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-violet-600" /> FWM Copilot
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Copilot needs a tenant context. Open any School from the dashboard first — Copilot will then
              be available inline. Super-admins can also paste a tenant id below to start a session.
            </p>
            <input
              className="w-full border rounded h-9 px-2 text-sm"
              placeholder="Tenant ID (super-admin only)"
              data-testid="copilot-tenant-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                  localStorage.setItem('eventflow_last_tenant', e.currentTarget.value.trim());
                  window.location.reload();
                }
              }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto py-6 px-4 space-y-4" data-testid="copilot-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            FWM Copilot
          </h1>
          <p className="text-sm text-slate-500">
            Plan lessons, draft quizzes, summarise data — every action waits for your approval.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4 min-h-[640px]">
        <Card className="overflow-hidden">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Chat</CardTitle></CardHeader>
          <div className="h-[640px] flex flex-col">
            <CopilotChat tenantId={effectiveTenant} userRole={role}
              onProposal={() => setLastProposalAt(Date.now())} />
          </div>
        </Card>

        <ProposalsPanel tenantId={effectiveTenant} />
      </div>
    </div>
  );
}
